import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type AppRouterError, trpc } from '@/config/trpc';
import { parseOutlookDragEvent } from '@/shared/utils/outlookDragDrop';
import useIsMounted from '@/shared/hooks/useIsMounted';
import useNotification from '@/shared/hooks/UseNotification';

const MAILBOX_NOT_LINKED_ERROR_CODE = 'MailboxNotLinked';

const base64ToFile = (base64: string, fileName: string): File => {
  const binaryString = window.atob(base64.replace(/\s/g, ''));
  const bytes = new Uint8Array(Array.from(binaryString, (char) => char.charCodeAt(0)));

  return new File([bytes], fileName, { type: 'message/rfc822' });
};

const toEmlFileName = (subject: string) =>
  `${subject.replace(/[^a-zA-Z0-9_-]/g, '_') || 'email'}.eml`;

export type UseOutlookDropzone = {
  isResolvingOutlookEmail: boolean;
  onDragOver: (event: React.DragEvent) => void;
  onDrop: (event: React.DragEvent) => void;
};

const useOutlookDropzone = ({
  onEmailFileResolved,
}: {
  onEmailFileResolved: (file: File) => void;
}): UseOutlookDropzone => {
  const { t } = useTranslation(['common', 'errors']);
  const { sendNotification, sendDefaultError } = useNotification();
  const { isMountedRef } = useIsMounted();
  const trpcUtils = trpc.useUtils();

  const [isResolvingOutlookEmail, setIsResolvingOutlookEmail] = useState(false);

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();

    const { dataTransfer } = event;

    if (dataTransfer) {
      dataTransfer.dropEffect = 'copy';

      if (!Array.from(dataTransfer.types).includes('Files')) {
        event.stopPropagation();
      }
    }
  }, []);

  const onDrop = useCallback(
    async (event: React.DragEvent) => {
      const parsed = parseOutlookDragEvent(event);

      if (parsed.type !== 'outlook-link') {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      if (!parsed.userEmail) {
        sendNotification({ message: t('errors:failedToReadOutlookEmail'), variant: 'error' });
        return;
      }

      setIsResolvingOutlookEmail(true);

      try {
        const base64Eml = await trpcUtils.mailbox.messages.getOutlookEml.fetch({
          email: parsed.userEmail,
          emailId: parsed.itemId ?? null,
          subject: parsed.subject ?? '',
        });

        onEmailFileResolved(base64ToFile(base64Eml, toEmlFileName(parsed.subject ?? 'email')));
      } catch (error) {
        const trpcError = error as AppRouterError;

        if (trpcError.data?.apiErrorCode === MAILBOX_NOT_LINKED_ERROR_CODE) {
          sendNotification({
            message: t('errors:mailboxNotLinkedForOutlookDrop', { email: parsed.userEmail }),
            variant: 'error',
          });
        } else {
          sendDefaultError(error);
        }
      } finally {
        if (isMountedRef.current) {
          setIsResolvingOutlookEmail(false);
        }
      }
    },
    [isMountedRef, onEmailFileResolved, sendDefaultError, sendNotification, t, trpcUtils],
  );

  return { isResolvingOutlookEmail, onDragOver, onDrop };
};

export default useOutlookDropzone;
